import { useMemo, useState } from 'react';
import { Icon } from '../../../components/icons';
import { Card, Field, Input, cn } from '../../../components/ui';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

interface SubjectScore {
  /** 0–100 composite, higher is better. */
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  metrics: {
    length: number;
    wordCount: number;
    capsRatio: number;
    emojiCount: number;
    punctuationBursts: number;
    hasNumber: boolean;
    hasPowerWord: boolean;
    spamWordHits: string[];
  };
  /** Actionable feedback — surfaced one per row in the breakdown. */
  feedback: Array<{ severity: 'good' | 'warn' | 'bad'; label: string; detail: string }>;
}

const POWER_WORDS = [
  'how to', 'why', 'what', 'when', 'tips', 'guide', 'steps', 'reasons',
  'because', 'free', 'new', 'now', 'today', 'introducing', 'exclusive',
  'limited', 'last', 'first', 'best', 'top', 'breaking', 'just', 'announcing',
  'finally', 'thanks', 'congrats', 'update', 'invitation', 'invite',
];

const SPAM_WORDS = [
  'free', 'guarantee', 'urgent', 'act now', 'limited time', 'click here',
  'congratulations', 'winner', 'cash', '100% free', 'risk free',
  'buy now', 'order now', 'best price', 'cheap', 'discount', 'no cost',
  'amazing', 'incredible', 'miracle', 'viagra', 'casino', 'lottery',
];

const GRADE_TONE: Record<SubjectScore['grade'], string> = {
  A: 'text-allow',
  B: 'text-allow',
  C: 'text-verify',
  D: 'text-verify',
  F: 'text-review',
};

/**
 * Free subject-line tester. Pure client-side heuristic scoring — length, caps
 * ratio, emoji count, punctuation, power-word presence, spam-word presence.
 * Produces a 0-100 composite + an A–F grade for at-a-glance use.
 */
export function SubjectLineTesterPage() {
  const [subject, setSubject] = useState('');

  const result = useMemo(() => scoreSubject(subject), [subject]);

  const content: ToolPageContent = {
    slug: 'subject-line-tester',
    title: 'Email subject line tester',
    tagline: 'Grade your subject line on length, tone, and spam triggers — instantly.',
    eyebrow: 'Free tool · No signup · 100% in-browser',
    description:
      'Free email subject line tester. Score your subject on length, caps ratio, emoji use, punctuation, urgency, and known spam triggers. Get an instant A–F grade.',
    icon: <Icon.Type />,
    accent: 'verify',
    intro:
      'Type a subject. We score it on the six dimensions mailbox providers actually use — length sweet-spot, caps ratio, emoji density, punctuation, presence of power-words, and presence of spam triggers. Instant A–F grade + per-rule feedback.',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Your subject line</h2>
          <div className="mt-4">
            <Field label="Subject" hint="Paste or type. Anything up to 200 characters.">
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Your latest update from Acme"
                maxLength={200}
                autoFocus
              />
            </Field>
          </div>

          {subject && (
            <div className="mt-5 grid gap-1.5 text-[12.5px]">
              <Stat label="Characters" value={`${result.metrics.length} / 60 ideal · 70 max`} ok={result.metrics.length > 0 && result.metrics.length <= 70} />
              <Stat label="Words" value={`${result.metrics.wordCount}`} ok={result.metrics.wordCount >= 4 && result.metrics.wordCount <= 12} />
              <Stat label="Caps ratio" value={`${Math.round(result.metrics.capsRatio * 100)}% (≤ 30% ideal)`} ok={result.metrics.capsRatio <= 0.3} />
              <Stat label="Emojis" value={`${result.metrics.emojiCount} (0-1 ideal)`} ok={result.metrics.emojiCount <= 1} />
              <Stat label="Numbers present" value={result.metrics.hasNumber ? 'yes' : 'no'} ok={result.metrics.hasNumber} hint="Numbers lift open-rates ~ 15% on average." />
              <Stat label="Power words" value={result.metrics.hasPowerWord ? 'yes' : 'no'} ok={result.metrics.hasPowerWord} hint='"how to / new / today / introducing / …"' />
              <Stat label="Spam triggers" value={result.metrics.spamWordHits.length > 0 ? result.metrics.spamWordHits.join(', ') : 'none'} ok={result.metrics.spamWordHits.length === 0} />
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-sm font-semibold">Grade + feedback</h2>
          {!subject && (
            <p className="mt-4 text-[13px] text-muted">Type a subject on the left to grade it.</p>
          )}
          {subject && (
            <>
              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-faint">Composite score</div>
                  <div className={cn('mt-1 font-display text-5xl font-extrabold tabular-nums', GRADE_TONE[result.grade])}>
                    {result.score}
                    <span className="ml-1 text-base font-normal text-faint">/100</span>
                  </div>
                </div>
                <div className={cn('font-display text-7xl font-extrabold leading-none', GRADE_TONE[result.grade])}>
                  {result.grade}
                </div>
              </div>

              <ul className="mt-5 divide-y divide-bordersoft">
                {result.feedback.map((f) => (
                  <li key={f.label} className="flex items-start gap-3 py-3">
                    <span
                      className={cn(
                        'mt-0.5 font-mono text-[14px]',
                        f.severity === 'good' && 'text-allow',
                        f.severity === 'warn' && 'text-verify',
                        f.severity === 'bad' && 'text-review',
                      )}
                    >
                      {f.severity === 'good' ? '✓' : f.severity === 'warn' ? '!' : '✗'}
                    </span>
                    <div>
                      <div className="text-[13.5px] font-medium">{f.label}</div>
                      <p className="mt-0.5 text-[12.5px] text-muted">{f.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'What makes a good email subject line',
      paragraphs: [
        'Mailbox providers use the subject as one of many features fed into their inbox-vs-spam classifier. They also expose snippet length to the user as the primary "should I open this" cue — Gmail truncates around 60 chars on desktop, ~30 on mobile.',
        'The sweet spot from large-scale studies (Mailchimp, Litmus, Campaign Monitor) is 4–7 words at 30–60 characters. Shorter than that lacks context; longer gets truncated. Subjects starting with the recipient\'s name or a number outperform average open rates by 10–20%.',
        'Avoid ALL-CAPS, multiple exclamation marks, more than one emoji, and any of the obvious spam triggers ("FREE", "URGENT", "ACT NOW"). One emoji at the start can help open-rates in B2C; three+ tank both opens and inbox placement.',
        'Personal tone beats marketing tone for cold outreach. "Quick question about your hiring plans" outperforms "🔥 EXCLUSIVE: Hiring tools for Q4!" by a wide margin in every test we\'ve seen.',
      ],
    },
    faq: [
      { q: 'What\'s the ideal length?', a: 'Roughly 30–60 characters / 4–7 words. Gmail truncates around 60 chars on desktop and ~30 on mobile, so any signal past that gets cut.' },
      { q: 'Are emojis good or bad?', a: 'Zero or one emoji is fine and can lift open rates in B2C. Two or more starts to read as spam to filters; three+ is a strong negative signal.' },
      { q: 'Why does it penalise ALL CAPS?', a: 'ALL CAPS in the subject is one of the strongest historical spam signals — Bayesian filters have been trained on it since 2003 and the weight is still high.' },
      { q: 'Why does it reward numbers?', a: 'Subjects with numbers ("3 tips", "the 2026 outlook", "$50 off") out-open subjects without by a measurable margin (Mailchimp, ~15%). Specificity feels concrete and credible.' },
      { q: 'Will an A guarantee inbox placement?', a: 'No. The subject is one signal among many — sender reputation, auth posture, content of the body, list hygiene all matter. A strong subject won\'t save bad fundamentals.' },
    ],
    relatedTools: [
      { slug: 'spam-words-checker', title: 'Spam words checker', tagline: 'Highlight every spam-trigger word in the body, not just the subject.' },
      { slug: 'email-spam-checker', title: 'Full email spam checker', tagline: 'Score the full email (subject + body + sender) against 15+ rules.' },
      { slug: 'dmarc-generator', title: 'DMARC record generator', tagline: 'Lock down sender auth so receivers don\'t need to second-guess you.' },
    ],
    ctaTitle: 'Measure actual inbox placement, not just guess',
    ctaBody:
      'Subject scoring is a heuristic. The real test is whether the message lands in the inbox or spam — at every major ESP. SieveGuard\'s Inbox Placement add-on sends to real seed accounts at Gmail, Outlook, Yahoo, ProtonMail, GMX and reports per-ESP.',
  };

  return <ToolPageLayout content={content} />;
}

function scoreSubject(subject: string): SubjectScore {
  const trimmed = subject.trim();
  const length = trimmed.length;
  const wordCount = (trimmed.match(/\b\w+\b/g) ?? []).length;
  const alpha = trimmed.replace(/[^A-Za-z]/g, '');
  const capsRatio = alpha.length === 0 ? 0 : alpha.replace(/[^A-Z]/g, '').length / alpha.length;
  const emojiCount = (trimmed.match(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu) ?? []).length;
  const punctuationBursts = (trimmed.match(/[!?]{2,}/g) ?? []).length;
  const hasNumber = /\d/.test(trimmed);
  const lower = trimmed.toLowerCase();
  const hasPowerWord = POWER_WORDS.some((w) => lower.includes(w));
  const spamWordHits = SPAM_WORDS.filter((w) => lower.includes(w));

  const feedback: SubjectScore['feedback'] = [];
  let score = 100;

  // Length — peaks at 30-60 chars.
  if (length === 0) {
    feedback.push({ severity: 'bad', label: 'Empty subject', detail: 'No subject — filters treat empty subjects as highly suspicious.' });
    score -= 50;
  } else if (length < 20) {
    feedback.push({ severity: 'warn', label: 'Short subject', detail: `Only ${length} characters. Below ~20 chars often reads as a placeholder.` });
    score -= 10;
  } else if (length > 70) {
    feedback.push({ severity: 'bad', label: 'Long subject', detail: `${length} characters — Gmail truncates around 60 on desktop, 30 on mobile. The end of your subject is invisible.` });
    score -= 15;
  } else if (length >= 30 && length <= 60) {
    feedback.push({ severity: 'good', label: 'Length in the sweet spot', detail: `${length} characters — fits in Gmail's preview without truncation on desktop.` });
  } else {
    feedback.push({ severity: 'good', label: 'Reasonable length', detail: `${length} characters — acceptable, sweet spot is 30–60.` });
  }

  // Caps ratio.
  if (capsRatio > 0.5) {
    feedback.push({ severity: 'bad', label: 'Too much UPPERCASE', detail: `${Math.round(capsRatio * 100)}% of letters are capitalised — strongest historical spam signal.` });
    score -= 25;
  } else if (capsRatio > 0.3) {
    feedback.push({ severity: 'warn', label: 'High caps ratio', detail: `${Math.round(capsRatio * 100)}% capitalised — keep below 30%.` });
    score -= 10;
  }

  // Emojis.
  if (emojiCount >= 3) {
    feedback.push({ severity: 'bad', label: 'Emoji-heavy', detail: `${emojiCount} emojis — three or more starts to trip spam filters.` });
    score -= 15;
  } else if (emojiCount === 2) {
    feedback.push({ severity: 'warn', label: 'Multiple emojis', detail: '2 emojis — one is fine, two starts to look promotional.' });
    score -= 5;
  } else if (emojiCount === 1) {
    feedback.push({ severity: 'good', label: 'One emoji', detail: 'A single emoji at the right spot can lift open rates in B2C.' });
  }

  // Punctuation bursts.
  if (punctuationBursts > 0) {
    feedback.push({ severity: 'bad', label: 'Excessive punctuation', detail: 'Multiple !!! or ??? in the subject — classic spam signal.' });
    score -= 12;
  }

  // Numbers — small bonus.
  if (hasNumber) {
    feedback.push({ severity: 'good', label: 'Contains a number', detail: 'Specific numbers lift open rates ~ 15% on average. Good signal.' });
  }

  // Power words.
  if (hasPowerWord) {
    feedback.push({ severity: 'good', label: 'Uses a power word', detail: 'Words like "how to / new / today / introducing" frame the value clearly.' });
  } else if (length > 0) {
    feedback.push({ severity: 'warn', label: 'No power word', detail: 'Consider leading with a verb or number — "How we …", "3 ways to …", "Introducing …" all out-open generic subjects.' });
    score -= 5;
  }

  // Spam words.
  if (spamWordHits.length > 0) {
    feedback.push({ severity: 'bad', label: 'Spam-trigger words', detail: `Detected: ${spamWordHits.join(', ')}. Rewrite without these.` });
    score -= Math.min(30, spamWordHits.length * 8);
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  const grade: SubjectScore['grade'] =
    score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 55 ? 'C' : score >= 40 ? 'D' : 'F';

  return {
    score,
    grade,
    metrics: { length, wordCount, capsRatio, emojiCount, punctuationBursts, hasNumber, hasPowerWord, spamWordHits },
    feedback,
  };
}

function Stat({ label, value, ok, hint }: { label: string; value: string; ok: boolean; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-md border border-bordersoft bg-bg px-3 py-1.5">
      <span className="text-muted">{label}</span>
      <span
        className={cn(
          'font-mono text-[12px]',
          ok ? 'text-allow' : 'text-verify',
        )}
        title={hint}
      >
        {value}
      </span>
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default SubjectLineTesterPage;
